/**
 * MODULE: _shared/lib/frontend/api/authTokenRefresh.ts
 * PURPOSE: One-shot JWT refresh via gateway POST /api/v1/auth/token/refresh (deduped, cooldown on failure)
 * DEPENDENCIES: ./config (**getApiBaseUrl**)
 * PUBLIC_API: refreshBraznAccessTokenOnce, isBraznRefreshCooldownActive, clearBraznSessionTokens
 * SIDE_EFFECTS: Reads or writes **localStorage** **refresh_token** and **access_token**
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-06-04 [2.2.1273] — **BraznRefreshOptions.force** for session modal extend; single-flight refresh; 60s cooldown after failed refresh.
 * RECENT_PRIOR: 2026-06-04 — Clear tokens on refresh **401**.
 */

import { getApiBaseUrl, isAccessTokenExpired } from './config';

const REFRESH_KEY = 'refresh_token';
const ACCESS_KEY = 'access_token';
const TENANT_KEY = 'tenant_id';

const REFRESH_FAILURE_COOLDOWN_MS = 60_000;

/** Dispatched when tokens change so shell hooks can re-read localStorage without useAuth (HMR-safe). */
export const BRAZN_SESSION_UPDATED_EVENT = 'brazn:session-updated';

export type BraznRefreshOptions = {
  /** When true, always POST refresh even if access JWT is not expired (session extend modal). */
  force?: boolean;
};

let refreshInFlight: Promise<boolean> | null = null;
let refreshBlockedUntilMs = 0;

function notifySessionUpdated(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(BRAZN_SESSION_UPDATED_EVENT));
}

/** True after a failed refresh (especially **401**) — callers should skip new refresh attempts. */
export function isBraznRefreshCooldownActive(): boolean {
  return Date.now() < refreshBlockedUntilMs;
}

/** Remove stale session keys after refresh rejection (matches **AuthProvider** **clearAuth** keys). */
export function clearBraznSessionTokens(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(TENANT_KEY);
  notifySessionUpdated();
}

function redirectToAuthIfNeeded(): void {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (path.startsWith('/auth') || path.startsWith('/login')) return;
  window.location.assign('/auth?expired=true');
}

function markRefreshFailed(status?: number): void {
  refreshBlockedUntilMs = Date.now() + REFRESH_FAILURE_COOLDOWN_MS;
  if (status === 401) {
    clearBraznSessionTokens();
    redirectToAuthIfNeeded();
  } else {
    notifySessionUpdated();
  }
}

async function performRefresh(options?: BraznRefreshOptions): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (isBraznRefreshCooldownActive()) return false;

  const existingAccess = (localStorage.getItem(ACCESS_KEY) || '').trim();
  if (
    !options?.force &&
    existingAccess &&
    !isAccessTokenExpired(existingAccess)
  ) {
    return true;
  }

  const rt = (localStorage.getItem(REFRESH_KEY) || '').trim();
  if (!rt) return false;

  const base = getApiBaseUrl().replace(/\/$/, '');
  let res: Response;
  try {
    res = await fetch(`${base}/api/v1/auth/token/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    });
  } catch {
    markRefreshFailed();
    return false;
  }

  if (!res.ok) {
    markRefreshFailed(res.status);
    return false;
  }

  let data: unknown;
  try {
    data = await res.json();
  } catch {
    markRefreshFailed();
    return false;
  }

  const at =
    data && typeof data === 'object' && 'access_token' in data
      ? String((data as { access_token?: unknown }).access_token || '').trim()
      : '';
  if (!at) {
    markRefreshFailed();
    return false;
  }

  localStorage.setItem(ACCESS_KEY, at);
  const nextRt =
    data && typeof data === 'object' && 'refresh_token' in data
      ? String((data as { refresh_token?: unknown }).refresh_token || '').trim()
      : '';
  if (nextRt) {
    localStorage.setItem(REFRESH_KEY, nextRt);
  }
  refreshBlockedUntilMs = 0;
  notifySessionUpdated();
  return true;
}

/**
 * Exchange **refresh_token** for a new **access_token** (at most one in-flight request app-wide).
 * After failure, skips network for **REFRESH_FAILURE_COOLDOWN_MS** to avoid console spam.
 */
export async function refreshBraznAccessTokenOnce(
  options?: BraznRefreshOptions
): Promise<boolean> {
  if (isBraznRefreshCooldownActive()) return false;
  if (!refreshInFlight) {
    refreshInFlight = performRefresh(options).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}
